import { Fragment, isValidElement, type ReactNode } from 'react';

/**
 * ¿El nodo pinta algo? `estados`/`acciones` suelen llegar como un fragmento cuyos hijos son
 * todos condicionales; sin esta comprobación se dibujaría el bloque (y su borde) vacío.
 */
function tieneContenido(nodo: ReactNode): boolean {
  if (nodo === null || nodo === undefined || typeof nodo === 'boolean' || nodo === '') return false;
  if (Array.isArray(nodo)) return nodo.some(tieneContenido);
  if (isValidElement(nodo) && nodo.type === Fragment) {
    return tieneContenido((nodo.props as { children?: ReactNode }).children);
  }
  return true;
}

/** Un par "Etiqueta: valor" dentro de la tarjeta. */
export interface CampoTarjeta {
  etiqueta: string;
  /** Si viene vacío (null, undefined o cadena vacía) el campo NO se pinta. */
  valor: ReactNode;
}

/**
 * Tarjeta que reemplaza a UNA fila de tabla en pantallas chicas (< lg), para no obligar
 * al desplazamiento horizontal en el celular. Se usa dentro de un contenedor `lg:hidden`,
 * mientras la tabla original queda `hidden lg:block` (en escritorio nada cambia).
 *
 * Props:
 * - `titulo`: lo que identifica la fila (persona, tienda o campaña). Se pinta en negrita.
 * - `subtitulo`: dato secundario del título (distrito, cliente…). Opcional.
 * - `seleccion`: control de selección múltiple (checkbox); va ARRIBA, junto al título.
 * - `estados`: etiquetas de color de estado y alertas; reutiliza los mismos `<span>` de la tabla.
 * - `campos`: pares "Etiqueta: valor"; se omiten los vacíos y los `null`/`false` de la lista.
 * - `acciones`: botones y enlaces de la fila; van al pie y cada hijo directo recibe 44px de
 *   alto mínimo para tocarlo con el dedo.
 * - `atenuada`: fila secundaria (por ejemplo una FALTA), con el mismo gris que usa la tabla.
 *
 * El contenido siempre envuelve (`break-words`): nunca se corta ni se trunca con "…".
 */
export default function FilaTarjeta({
  titulo,
  subtitulo,
  seleccion,
  estados,
  campos = [],
  acciones,
  atenuada = false,
}: {
  titulo: ReactNode;
  subtitulo?: ReactNode;
  seleccion?: ReactNode;
  estados?: ReactNode;
  campos?: (CampoTarjeta | null | false | undefined)[];
  acciones?: ReactNode;
  atenuada?: boolean;
}) {
  const visibles = campos.filter(
    (c): c is CampoTarjeta =>
      !!c && c.valor !== null && c.valor !== undefined && c.valor !== '' && c.valor !== false,
  );
  return (
    <div
      className={`rounded border p-3 text-sm ${
        atenuada ? 'bg-gray-50 text-gray-600' : 'bg-white text-gray-900'
      }`}
    >
      <div className="flex items-start gap-2">
        {seleccion && <div className="flex shrink-0 items-center">{seleccion}</div>}
        <div className="min-w-0 flex-1">
          <p className="break-words font-semibold">{titulo}</p>
          {subtitulo && <p className="break-words text-xs text-gray-500">{subtitulo}</p>}
        </div>
      </div>
      {tieneContenido(estados) && (
        <div className="mt-2 flex flex-wrap items-center gap-1">{estados}</div>
      )}
      {visibles.length > 0 && (
        <dl className="mt-2 space-y-1">
          {visibles.map((c) => (
            <div key={c.etiqueta} className="flex flex-wrap gap-x-1">
              <dt className="text-gray-500">{c.etiqueta}:</dt>
              <dd className="min-w-0 break-words">{c.valor}</dd>
            </div>
          ))}
        </dl>
      )}
      {tieneContenido(acciones) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-1 [&>*]:inline-flex [&>*]:min-h-[44px] [&>*]:items-center">
          {acciones}
        </div>
      )}
    </div>
  );
}
